import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useTemuPageShopScope, stackShopDimensionTabs } from '@/temu-platform/hooks/useTemuPageShopScope';
import TemuOpsListToolbar, { downloadCsv } from '@/temu-platform/components/TemuOpsListToolbar';
import TemuOpsPager, { TEMU_OPS_DEFAULT_PAGE_SIZE } from '@/temu-platform/components/TemuOpsPager';
import TemuSyncProgressSlot from '@/temu-platform/components/TemuSyncProgressSlot';
import { ProductFilterCard, ProductListCard } from '@/temu-platform/modules/products/components/ProductPageCards';
import TemuMarketingActivityDrawer from '@/temu-platform/modules/ops/TemuMarketingActivityDrawer';
import MarketingSitesCell from '@/temu-platform/modules/ops/components/MarketingSitesCell';
import MarketingEnrollSessionsModal from '@/temu-platform/modules/ops/MarketingEnrollSessionsModal';
import HoverCopyField from '@/temu-platform/modules/products/components/HoverCopyField';
import TemuProductThumb from '@/temu-platform/modules/products/components/TemuProductThumb';
import { useGuardedRefresh } from '@/temu-platform/hooks/useGuardedRefresh';
import { useTemuSyncJobs } from '@/temu-platform/context/TemuSyncJobContext';
import { buildTemuPath, openTemuPathInNewTab } from '@/temu-platform/constants/temuRoutes';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { formatSiteListSyncedAt } from '@/temu-platform/utils/temuSiteList';
import { togglePageSelection } from '@/temu-platform/utils/temuPageSelection';
import { formatMarketingSites } from '@/temu-platform/utils/temuMarketingDisplay';
import { fetchAllPages } from '@/temu-platform/utils/temuExportHelpers';
import { buildOpsExportMenu, opsExportLabel } from '@/temu-platform/utils/buildOpsExportMenu';
import { showToast } from '@/utils/toast';
import '@/styles/temu-platform/temu-product-ops.css';

const ACTIVITY_TYPE_TABS = [
  { value: '', label: '全部', labelKey: 'tiktokCatalog.all' },
  { value: '1', label: '限时秒杀', labelKey: 'marketing.tmt1' },
  { value: '5', label: '大促活动', labelKey: 'marketing.tmt5' },
  { value: 'thematic', label: '活动专题', labelKey: 'marketing.tmtThematic' },
  { value: '21', label: '超级秒杀', labelKey: 'marketing.tmt21' },
  { value: '27', label: '清仓甩卖', labelKey: 'marketing.tmt27' },
  { value: '31', label: '超级万人团', labelKey: 'marketing.tmt31' },
];

/** 类型列展示名（13/14/101/127 平台文档统称「活动专题」） */
const ACTIVITY_TYPE_LABEL_BY_CODE = {
  1: '限时秒杀',
  5: '大促活动',
  13: '活动专题',
  14: '活动专题',
  21: '超级秒杀',
  27: '清仓甩卖',
  31: '超级万人团',
  101: '活动专题',
  127: '活动专题',
};

const THEMATIC_ACTIVITY_TYPE_CODES = ['13', '14', '101', '127'];

function activityTypeDisplayLabel(row) {
  const code = row?.activityType != null ? String(row.activityType).trim() : '';
  const labelKeyByCode = {
    1: 'tmt1',
    5: 'tmt5',
    13: 'tmtThematic',
    14: 'tmtThematic',
    21: 'tmt21',
    27: 'tmt27',
    31: 'tmt31',
    101: 'tmtThematic',
    127: 'tmtThematic',
  };
  const labelKeyByText = {
    限时秒杀: 'tmt1',
    大促活动: 'tmt5',
    活动专题: 'tmtThematic',
    超级秒杀: 'tmt21',
    清仓甩卖: 'tmt27',
    超级万人团: 'tmt31',
  };
  if (code && labelKeyByCode[code]) {
    return i18n.t(`marketing.${labelKeyByCode[code]}`, {
      ns: 'workbench',
      defaultValue: ACTIVITY_TYPE_LABEL_BY_CODE[code] || code,
    });
  }
  const raw = String(row?.activityTypeLabel || row?.type || '').trim();
  if (labelKeyByText[raw]) {
    return i18n.t(`marketing.${labelKeyByText[raw]}`, { ns: 'workbench', defaultValue: raw });
  }
  if (raw && labelKeyByCode[raw]) {
    return i18n.t(`marketing.${labelKeyByCode[raw]}`, {
      ns: 'workbench',
      defaultValue: ACTIVITY_TYPE_LABEL_BY_CODE[raw] || raw,
    });
  }
  return raw || code || '—';
}

function enrollSourceDisplayLabel(row) {
  const label = row?.enrollSourceLabel;
  if (label != null && String(label).trim() && !/^\d+$/.test(String(label).trim())) {
    return String(label).trim();
  }
  const code = row?.enrollSource ?? row?.status;
  const n = Number(code);
  if (n === 0) return i18n.t('marketing.srcInvite', { ns: 'workbench' });
  if (n === 1) return i18n.t('marketing.srcSelf', { ns: 'workbench' });
  if (code == null || code === '') return '—';
  return String(code);
}

const ENROLL_STATUS_FILTER_OPTIONS = [
  { value: '', label: '全部报名状态', labelKey: 'marketing.esAll' },
  { value: '1', label: '报名中', labelKey: 'marketing.es1' },
  { value: '2', label: '报名失败', labelKey: 'marketing.es2' },
  { value: '3', label: '报名成功待分配场次', labelKey: 'marketing.es3' },
  { value: '4', label: '报名成功已分配场次', labelKey: 'marketing.es4' },
  { value: '5', label: '报名活动已结束', labelKey: 'marketing.es5' },
  { value: '6', label: '报名活动已下线', labelKey: 'marketing.es6' },
];

const EMPTY_FILTER_DRAFT = {
  keyword: '',
  enrollSource: '',
  enrollStatus: '',
  enrollTimeBegin: '',
  enrollTimeEnd: '',
};

const EMPTY_APPLIED = {
  keyword: '',
  activityType: '',
  sessionStatus: '',
  enrollSource: '',
  enrollStatus: '',
  enrollTimeBegin: '',
  enrollTimeEnd: '',
};

function dayStartEpochSeconds(dateStr) {
  if (!dateStr) return undefined;
  const ms = Date.parse(`${dateStr}T00:00:00+08:00`);
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : undefined;
}

function dayEndEpochSeconds(dateStr) {
  if (!dateStr) return undefined;
  const ms = Date.parse(`${dateStr}T23:59:59+08:00`);
  return Number.isFinite(ms) ? Math.floor(ms / 1000) : undefined;
}

/** 报名记录场次状态（对齐平台 sessionStatus） */
const ENROLL_STATUS_TABS = [
  { value: '', label: '全部', labelKey: 'tiktokCatalog.all' },
  { value: '1', label: '未开始', labelKey: 'marketing.ss1' },
  { value: '2', label: '进行中', labelKey: 'marketing.ss2' },
  { value: '3', label: '已结束', labelKey: 'marketing.ss3' },
  { value: '4', label: '报名失败', labelKey: 'marketing.ss4' },
  { value: '5', label: '已售罄', labelKey: 'marketing.ss5' },
  { value: '6', label: '已下线', labelKey: 'marketing.ss6' },
];

const VIEW_META = {
  activities: {
    title: '活动列表',
    titleKey: 'marketing.actList',
    syncKinds: ['marketing'],
    syncKind: 'marketing',
    quotaKind: 'marketing',
    dumpKind: 'marketing',
    colSpan: 8,
    syncHint: '同步可报名活动列表、活动详情与场次（并行拉取）；本页可报名。',
    syncHintKey: 'marketing.temuSyncAct',
    emptyNever: '尚未同步可报名活动',
    emptyNeverKey: 'marketing.neverAct',
    emptyNone: '暂无可报名活动',
    emptyNoneKey: 'marketing.noneAct',
    loadingLabel: '正在查询活动…',
    loadingLabelKey: 'marketing.queryAct',
    keywordPlaceholder: '活动名称 / 专题ID',
    keywordPlaceholderKey: 'marketing.temuKwAct',
    selectAllLabelKey: 'marketing.selectAct',
    pagerLabelKey: 'marketing.pagerAct',
    exportFilePrefix: 'temu-marketing-activities',
    selectAllLabel: '全选活动',
    pagerLabel: '活动列表分页',
  },
  enrolls: {
    title: '报名记录',
    titleKey: 'marketing.enrollList',
    syncKinds: ['marketingProduct'],
    syncKind: 'marketingProduct',
    quotaKind: 'marketing',
    dumpKind: 'marketing-product',
    colSpan: 9,
    syncHint: '并行同步报名记录、活动商品与活动场次；本页仅查看，报名请到「活动列表」。',
    syncHintKey: 'marketing.temuSyncEnroll',
    emptyNever: '尚未同步报名记录',
    emptyNeverKey: 'marketing.neverEnroll',
    emptyNone: '暂无报名记录',
    emptyNoneKey: 'marketing.noneEnroll',
    loadingLabel: '正在查询报名记录…',
    loadingLabelKey: 'marketing.queryEnroll',
    keywordPlaceholder: '专题名 / 专题ID / SKC / 货品ID',
    keywordPlaceholderKey: 'marketing.temuKwEnroll',
    selectAllLabelKey: 'marketing.selectEnroll',
    pagerLabelKey: 'marketing.pagerEnroll',
    exportFilePrefix: 'temu-marketing-enrolls',
    selectAllLabel: '全选报名记录',
    pagerLabel: '报名记录分页',
  },
};

function extractListPayload(res) {
  if (!res || typeof res !== 'object') {
    return {
      items: [],
      total: 0,
      lastSyncedAt: null,
      neverSynced: true,
      typeCounts: { all: 0 },
      statusCounts: { all: 0 },
    };
  }
  const items = Array.isArray(res.items)
    ? res.items
    : (Array.isArray(res.content) ? res.content : (Array.isArray(res.data) ? res.data : []));
  const total = Number.isFinite(Number(res.totalElements))
    ? Number(res.totalElements)
    : (Number.isFinite(Number(res.total)) ? Number(res.total) : items.length);
  const lastSyncedAt = res.lastSyncedAt || res.syncedAt || null;
  const neverSynced = Boolean(res.neverSynced) || (!lastSyncedAt && items.length === 0);
  const typeCounts = res.typeCounts && typeof res.typeCounts === 'object'
    ? res.typeCounts
    : { all: total };
  const statusCounts = res.statusCounts && typeof res.statusCounts === 'object'
    ? res.statusCounts
    : { all: total };
  return { items, total, lastSyncedAt, neverSynced, typeCounts, statusCounts };
}

function rowKey(row, view, index = 0) {
  if (view === 'enrolls') {
    return String(row?.id || row?.enrollId || `enroll-${index}`);
  }
  return String(row?.id || row?.activityThematicId || row?.activityId || `activity-${index}`);
}

function formatMoney(value, currency) {
  if (value == null || value === '') return '—';
  const raw = String(value).trim();
  if (!raw) return '—';
  if (raw.startsWith('$') || raw.startsWith('¥') || raw.startsWith('€')) return raw;
  const prefix = currency === 'CNY' || currency === 'RMB' ? '¥' : (currency === 'USD' ? '$' : (currency ? `${currency} ` : '$'));
  return `${prefix}${raw}`;
}

/** 活动价 / 供货价 → 折扣（如 300/346 ≈ 8.7折） */
function parsePriceNumber(value) {
  if (value == null || value === '') return null;
  const n = Number(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) && n > 0 ? n : null;
}

function formatActivityDiscount(activityPrice, supplyPrice) {
  const activity = parsePriceNumber(activityPrice);
  const supply = parsePriceNumber(supplyPrice);
  if (activity == null || supply == null) return null;
  // 无减价：按原价展示；有减价时不得四舍五入成「10折」
  if (activity >= supply) return '原价';
  const zhe = (activity / supply) * 10;
  if (!Number.isFinite(zhe) || zhe <= 0) return null;
  // 接近 10 折时保留两位，避免 326/327 被收成 10 折
  const text = zhe >= 9.95 ? zhe.toFixed(2) : (Math.round(zhe * 10) / 10).toFixed(1);
  return `${text}折`;
}

function enrollSkcItems(row) {
  if (Array.isArray(row?.skcItems) && row.skcItems.length) return row.skcItems;
  return [{
    colorLabel: row?.skcSummary || row?.sku || null,
    skcName: null,
    supplierCode: null,
    imageUrl: row?.productImageUrl || null,
    supplyPrice: null,
    activityPrice: null,
  }];
}

function activityStatusTone(labelOrCode) {
  const raw = String(labelOrCode ?? '').trim();
  if (raw === '1' || raw === '未开始') return 'pending';
  if (raw === '2' || raw === '进行中') return 'success';
  if (raw === '3' || raw === '已结束') return 'offline';
  if (raw === '4' || raw === '报名失败') return 'failed';
  if (raw === '5' || raw === '已售罄') return 'appeal';
  if (raw === '6' || raw === '已下线') return 'offline';
  return 'pending';
}

function enrollStatusTone(labelOrCode) {
  const raw = String(labelOrCode ?? '').trim();
  if (raw === '1' || raw.includes('报名中')) return 'pending';
  if (raw === '2' || raw.includes('报名失败')) return 'failed';
  if (raw === '3' || raw.includes('待分配')) return 'pending';
  if (raw === '4' || raw.includes('已分配') || raw.includes('报名成功')) return 'success';
  if (raw === '5' || raw.includes('已结束')) return 'offline';
  if (raw === '6' || raw.includes('已下线')) return 'offline';
  return activityStatusTone(raw);
}

function soldStatusTone(code) {
  const n = Number(code);
  if (n === 1) return 'warn';
  if (n === 2) return 'danger';
  return 'ok';
}

function toExportRows(view, source) {
  if (view === 'enrolls') {
    return source.map((row) => ({
      productName: row.productName || '',
      productId: row.productId ?? '',
      skcSummary: row.skcSummary || row.sku || '',
      supplyPrice: (enrollSkcItems(row).map((s) => s.supplyPrice).filter(Boolean).join(' / ')) || '',
      activityPrice: (enrollSkcItems(row).map((s) => s.activityPrice).filter(Boolean).join(' / ')) || '',
      enrollTime: row.enrollTime || '',
      activityThematicName: row.activityThematicName || row.name || '',
      sessionStartTime: row.sessionStartTime || row.startTime || '',
      sessionEndTime: row.sessionEndTime || row.endTime || '',
      activityStatus: row.activityStatusLabel || row.sessionStatusLabel || '',
      enrollStatus: row.enrollStatusLabel || '',
      activityStock: row.activityStock ?? '',
      remainingActivityStock: row.remainingActivityStock ?? '',
      soldStatus: row.soldStatusLabel || '',
    }));
  }
  return source.map((row) => ({
    name: row.name || row.activityName || '',
    activityThematicId: row.activityThematicId || row.activityId || '',
      activityType: activityTypeDisplayLabel(row),
      enrollSource: enrollSourceDisplayLabel(row),
    enrollDeadline: row.enrollDeadline || '',
    startTime: row.startTime || '',
    endTime: row.endTime || '',
    sites: formatMarketingSites(row).title,
  }));
}

function exportColumns(view) {
  if (view === 'enrolls') {
    return [
      'productName',
      'productId',
      'skcSummary',
      'supplyPrice',
      'activityPrice',
      'enrollTime',
      'activityThematicName',
      'sessionStartTime',
      'sessionEndTime',
      'activityStatus',
      'enrollStatus',
      'activityStock',
      'remainingActivityStock',
      'soldStatus',
    ];
  }
  return [
    'name',
    'activityThematicId',
    'activityType',
    'enrollSource',
    'enrollDeadline',
    'startTime',
    'endTime',
    'sites',
  ];
}

/**
 * 营销活动子页：activities | enrolls
 */
export default function TemuMarketingPage({ track = 'semi', view = 'activities' }) {
  const { t } = useTranslation('workbench');
  const pageView = VIEW_META[view] ? view : 'activities';
  const meta = VIEW_META[pageView];
  const { shopDimensionTabs, concreteScopeId } = useTemuPageShopScope();
  const shopId = concreteScopeId || '';
  const { startJob, onTrackDone, isBusy } = useTemuSyncJobs();

  const [filterDraft, setFilterDraft] = useState({ ...EMPTY_FILTER_DRAFT });
  const [applied, setApplied] = useState({ ...EMPTY_APPLIED });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TEMU_OPS_DEFAULT_PAGE_SIZE);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [typeCounts, setTypeCounts] = useState({ all: 0 });
  const [statusCounts, setStatusCounts] = useState({ all: 0 });
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [neverSynced, setNeverSynced] = useState(true);
  const [error, setError] = useState('');
  const [drawerActivity, setDrawerActivity] = useState(null);
  const [sessionsModal, setSessionsModal] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const selectedRowsRef = useRef(new Map());

  const openEnrollInNewTab = useCallback((row) => {
    if (!shopId) {
      showToast(t('marketing.pickTemu'), 'warning');
      return;
    }
    const thematicId = row?.activityThematicId || row?.activityId;
    if (thematicId == null || thematicId === '') {
      showToast(fixedUi("活动专题 ID 无效"), 'warning');
      return;
    }
    if (row?.activityType == null || row?.activityType === '') {
      showToast(fixedUi("活动类型无效"), 'warning');
      return;
    }
    const q = new URLSearchParams();
    q.set('shopId', String(shopId));
    q.set('activityType', String(row.activityType));
    const name = row.name || row.activityName;
    if (name) q.set('name', String(name));
    const typeLabel = row.activityTypeLabel || row.type;
    if (typeLabel) q.set('typeLabel', String(typeLabel));
    const path = `${buildTemuPath(track, `marketing/enroll/${thematicId}`)}?${q.toString()}`;
    openTemuPathInNewTab(path);
  }, [shopId, track, t]);

  const lastSyncedLabel = useMemo(() => {
    if (!lastSyncedAt) return t('tiktokCatalog.notSynced');
    try {
      return t('marketing.syncedAt', { time: formatSiteListSyncedAt(lastSyncedAt) });
    } catch {
      return t('marketing.syncedAt', { time: String(lastSyncedAt) });
    }
  }, [lastSyncedAt, t]);

  const selectableKeys = useMemo(
    () => rows.map((row, index) => rowKey(row, pageView, index)).filter(Boolean),
    [rows, pageView],
  );

  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = useCallback(() => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  }, [selectableKeys, allSelected]);

  const toggleOne = useCallback((key, row) => {
    if (!key) return;
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
        selectedRowsRef.current.delete(key);
      } else {
        next.add(key);
        if (row) selectedRowsRef.current.set(key, row);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    rows.forEach((row, index) => {
      const key = rowKey(row, pageView, index);
      if (selectedKeys.has(key)) selectedRowsRef.current.set(key, row);
    });
  }, [rows, selectedKeys, pageView]);

  useEffect(() => {
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
  }, [shopId, pageView, applied]);

  useEffect(() => {
    setPage(1);
  }, [shopId, pageView]);

  const resolveSelectedRows = useCallback(() => (
    Array.from(selectedKeys)
      .map((key) => selectedRowsRef.current.get(key))
      .filter(Boolean)
  ), [selectedKeys]);

  const clearSelection = useCallback(() => {
    setSelectedKeys(new Set());
    selectedRowsRef.current.clear();
  }, []);

  const exportRows = useCallback((source) => {
    if (!source.length) {
      showToast(fixedUi("暂无可导出数据"), 'warning');
      return;
    }
    downloadCsv(
      `${meta.exportFilePrefix}-${Date.now()}.csv`,
      exportColumns(pageView),
      toExportRows(pageView, source),
    );
    showToast(`已导出 ${source.length} 条`, 'success');
  }, [meta.exportFilePrefix, pageView]);

  const exportFiltered = useCallback(async () => {
    if (!shopId) {
      showToast(t('marketing.pickTemu'), 'warning');
      return;
    }
    if (exporting) return;
    setExporting(true);
    try {
      const filters = applied;
      const currentView = pageView;
      const all = await fetchAllPages(async (pageNo, size) => {
        const params = {
          page: Math.max(0, pageNo - 1),
          size,
          keyword: filters.keyword || undefined,
          activityType: filters.activityType || undefined,
        };
        let res;
        if (currentView === 'enrolls') {
          res = await temuPlatformAPI.listMarketingEnrolls(shopId, {
            ...params,
            sessionStatus: filters.sessionStatus === '' ? undefined : Number(filters.sessionStatus),
            enrollStatus: filters.enrollStatus === '' ? undefined : Number(filters.enrollStatus),
            enrollTimeBegin: dayStartEpochSeconds(filters.enrollTimeBegin),
            enrollTimeEnd: dayEndEpochSeconds(filters.enrollTimeEnd),
          });
        } else {
          res = await temuPlatformAPI.listMarketing(shopId, {
            ...params,
            enrollSource: filters.enrollSource === '' ? undefined : Number(filters.enrollSource),
          });
        }
        const payload = extractListPayload(res);
        return { items: payload.items, total: payload.total };
      });
      exportRows(all);
    } catch (e) {
      showToast(e?.message || fixedUi("导出筛选失败"), 'error');
    } finally {
      setExporting(false);
    }
  }, [shopId, exporting, applied, pageView, exportRows, t]);

  const copySelectedIds = useCallback(async () => {
    const selected = resolveSelectedRows();
    if (!selected.length) {
      showToast(fixedUi("请先勾选数据"), 'warning');
      return;
    }
    const ids = selected.map((row, index) => {
      if (pageView === 'enrolls') {
        return String(row.productId || row.enrollId || row.id || rowKey(row, pageView, index));
      }
      return String(row.activityThematicId || row.activityId || row.id || rowKey(row, pageView, index));
    }).filter(Boolean);
    const unique = [...new Set(ids)];
    const text = unique.join('\n');
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      showToast(`已复制 ${unique.length} 个 ID`, 'success');
    } catch {
      showToast(fixedUi("复制失败"), 'error');
    }
  }, [pageView, resolveSelectedRows]);

  const batchOpenEnroll = useCallback(() => {
    const selected = resolveSelectedRows();
    if (!selected.length) {
      showToast(fixedUi("请先勾选活动"), 'warning');
      return;
    }
    const MAX_TABS = 5;
    if (selected.length > MAX_TABS) {
      showToast(`一次最多打开 ${MAX_TABS} 个报名页，请减少选中数量`, 'warning');
      return;
    }
    selected.forEach((row) => openEnrollInNewTab(row));
    showToast(`已打开 ${selected.length} 个报名页`, 'success');
  }, [openEnrollInNewTab, resolveSelectedRows]);

  const batchViewActivity = useCallback(() => {
    const selected = resolveSelectedRows();
    if (selected.length !== 1) {
      showToast(t('marketing.oneActivity'), 'warning');
      return;
    }
    setDrawerActivity(selected[0]);
  }, [resolveSelectedRows, t]);

  const batchOpsMenu = useMemo(() => {
    const empty = selectedKeys.size === 0;
    const copyLabel = pageView === 'activities' ? t('marketing.copyThematic') : t('marketing.copyGoods');
    const items = [
      {
        key: 'copy-ids',
        label: copyLabel,
        disabled: empty,
        onClick: copySelectedIds,
      },
      {
        key: 'export-selected-batch',
        label: t('marketing.exportSelected'),
        disabled: empty,
        onClick: () => exportRows(resolveSelectedRows()),
      },
    ];
    if (pageView === 'activities') {
      items.unshift(
        {
          key: 'batch-enroll',
          label: '批量打开报名',
          disabled: empty,
          onClick: batchOpenEnroll,
        },
        {
          key: 'batch-view',
          label: t('marketing.viewSel'),
          disabled: selectedKeys.size !== 1,
          onClick: batchViewActivity,
        },
      );
    }
    items.push({
      key: 'clear-selection',
      label: t('marketing.clearSel'),
      disabled: empty,
      onClick: clearSelection,
    });
    return items;
  }, [
    selectedKeys.size,
    pageView,
    copySelectedIds,
    exportRows,
    resolveSelectedRows,
    batchOpenEnroll,
    batchViewActivity,
    clearSelection,
    t,
  ]);

  const onRowClickSelect = useCallback((event, key, row) => {
    if (event.target.closest('button, a, input, label, .tpo-marketing-list__ops')) return;
    toggleOne(key, row);
  }, [toggleOne]);

  const loadList = useCallback(async (sid, filters, currentView, pageNo, size) => {
    if (!sid) {
      setRows([]);
      setTotal(0);
      setTypeCounts({ all: 0 });
      setStatusCounts({ all: 0 });
      setNeverSynced(true);
      setLastSyncedAt(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = {
        page: Math.max(0, (Number(pageNo) || 1) - 1),
        size: Math.max(1, Number(size) || TEMU_OPS_DEFAULT_PAGE_SIZE),
        keyword: filters.keyword || undefined,
        activityType: filters.activityType || undefined,
      };
      let res;
      if (currentView === 'enrolls') {
        res = await temuPlatformAPI.listMarketingEnrolls(sid, {
          ...params,
          sessionStatus: filters.sessionStatus === '' ? undefined : Number(filters.sessionStatus),
          enrollStatus: filters.enrollStatus === '' ? undefined : Number(filters.enrollStatus),
          enrollTimeBegin: dayStartEpochSeconds(filters.enrollTimeBegin),
          enrollTimeEnd: dayEndEpochSeconds(filters.enrollTimeEnd),
        });
      } else {
        res = await temuPlatformAPI.listMarketing(sid, {
          ...params,
          enrollSource: filters.enrollSource === '' ? undefined : Number(filters.enrollSource),
        });
      }
      const payload = extractListPayload(res);
      setRows(payload.items);
      setTotal(payload.total);
      setTypeCounts(payload.typeCounts);
      setStatusCounts(payload.statusCounts);
      setLastSyncedAt(payload.lastSyncedAt);
      setNeverSynced(payload.neverSynced);
    } catch (e) {
      setRows([]);
      setTotal(0);
      setTypeCounts({ all: 0 });
      setStatusCounts({ all: 0 });
      setError(e?.message || t('marketing.loadFail', { title: t(meta.titleKey) }));
      showToast(e?.message || t('marketing.loadFail', { title: t(meta.titleKey) }), 'error');
    } finally {
      setLoading(false);
    }
  }, [meta.titleKey, t]);

  useEffect(() => {
    loadList(shopId, applied, pageView, page, pageSize);
  }, [shopId, applied, pageView, page, pageSize, loadList]);

  const onQuery = () => {
    setPage(1);
    setApplied((prev) => ({
      ...prev,
      keyword: String(filterDraft.keyword || '').trim(),
      enrollSource: String(filterDraft.enrollSource || ''),
      enrollStatus: String(filterDraft.enrollStatus || ''),
      enrollTimeBegin: String(filterDraft.enrollTimeBegin || ''),
      enrollTimeEnd: String(filterDraft.enrollTimeEnd || ''),
    }));
  };

  const onReset = () => {
    setFilterDraft({ ...EMPTY_FILTER_DRAFT });
    setPage(1);
    setApplied({ ...EMPTY_APPLIED });
  };

  const onActivityTypeTab = (value) => {
    setPage(1);
    setApplied((prev) => ({
      ...prev,
      activityType: String(value || ''),
    }));
  };

  const onEnrollStatusTab = (value) => {
    setPage(1);
    setApplied((prev) => ({
      ...prev,
      sessionStatus: String(value || ''),
    }));
  };

  const handleSync = useCallback(async () => {
    if (!shopId) {
      showToast(t('marketing.pickTemu'), 'warning');
      return;
    }
    try {
      const kind = pageView === 'enrolls' ? 'marketingProduct' : 'marketing';
      const started = await startJob({
        kind,
        shopId,
        label: t('publishRecord.syncUpdate'),
      });
      if (!started?.id) return;
      await new Promise((resolve) => {
        const unsub = onTrackDone(started.id, () => {
          unsub?.();
          resolve();
        });
      });
      await loadList(shopId, applied, pageView, page, pageSize);
    } catch (e) {
      if (e?.code === 'SYNC_IN_PROGRESS' || e?.code === 'ABORT') return;
      if (e?.message) showToast(e.message, 'error');
    }
  }, [shopId, applied, pageView, page, pageSize, loadList, startJob, onTrackDone, t]);

  const {
    refresh: onSync,
    refreshing: syncingLocal,
    cooldownSeconds: syncCooldown,
  } = useGuardedRefresh(handleSync, { minIntervalMs: 5000 });

  const syncing = syncingLocal || isBusy(meta.syncKind, shopId);

  const emptyTitle = useMemo(() => {
    if (!shopId) return t('returnList.pickShop');
    if (loading) return t('returnList.loading');
    if (neverSynced && rows.length === 0) return t(meta.emptyNeverKey);
    if (rows.length === 0) return t(meta.emptyNoneKey);
    return t('freightLib.noData');
  }, [shopId, loading, neverSynced, rows.length, meta, t]);

  const emptyHint = useMemo(() => {
    if (!shopId) return '';
    if (neverSynced && rows.length === 0 && !loading) {
      if (pageView === 'enrolls') {
        return fixedUi('请先同步「活动列表」，再在本页点击「拉取 TEMU 后台数据」并行拉取报名记录、活动商品与场次。');
      }
      return fixedUi('点击「拉取 TEMU 后台数据」拉取可报名活动、详情与场次。');
    }
    const hasFilter = Boolean(
      applied.keyword
      || applied.activityType
      || applied.sessionStatus
      || applied.enrollSource
      || applied.enrollStatus
      || applied.enrollTimeBegin
      || applied.enrollTimeEnd
    );
    if (rows.length === 0 && !loading && hasFilter) {
      return t('marketing.adjustFilter');
    }
    return '';
  }, [shopId, neverSynced, rows.length, loading, applied, pageView, t]);

  const typeTabs = useMemo(() => (
    <div className="tpo-status-tabs" role="tablist" aria-label={t('marketing.typeAria')}>
      {ACTIVITY_TYPE_TABS.map((tab) => {
        const countKey = tab.value === '' ? 'all' : tab.value;
        let count = Number(typeCounts?.[countKey] ?? 0);
        if (tab.value === 'thematic') {
          count = THEMATIC_ACTIVITY_TYPE_CODES.reduce(
            (sum, code) => sum + Number(typeCounts?.[code] ?? 0),
            0,
          );
        }
        const active = String(applied.activityType || '') === String(tab.value);
        return (
          <button
            key={tab.value || 'all'}
            type="button"
            role="tab"
            aria-selected={active}
            className={`tpo-status-tab${active ? ' tpo-status-tab--active' : ''}`}
            onClick={() => onActivityTypeTab(tab.value)}
          >
            {tab.labelKey ? t(tab.labelKey) : tab.label}
            <span className="tpo-status-tab__count">{Number.isFinite(count) ? count : 0}</span>
          </button>
        );
      })}
    </div>
  ), [applied.activityType, typeCounts, t]);

  const enrollStatusTabs = useMemo(() => (
    <div className="tpo-status-tabs" role="tablist" aria-label={fixedUi("报名场次状态")}>
      {ENROLL_STATUS_TABS.map((tab) => {
        const countKey = tab.value === '' ? 'all' : tab.value;
        const count = Number(statusCounts?.[countKey] ?? 0);
        const active = String(applied.sessionStatus || '') === String(tab.value);
        return (
          <button
            key={tab.value || 'all-status'}
            type="button"
            role="tab"
            aria-selected={active}
            className={`tpo-status-tab${active ? ' tpo-status-tab--active' : ''}`}
            onClick={() => onEnrollStatusTab(tab.value)}
          >
            {tab.labelKey ? t(tab.labelKey) : tab.label}
            <span className="tpo-status-tab__count">{Number.isFinite(count) ? count : 0}</span>
          </button>
        );
      })}
    </div>
  ), [applied.sessionStatus, statusCounts, t]);

  const listTabs = useMemo(
    () => stackShopDimensionTabs(
      shopDimensionTabs,
      pageView === 'enrolls' ? (
        <>
          {typeTabs}
          {enrollStatusTabs}
        </>
      ) : typeTabs,
    ),
    [shopDimensionTabs, pageView, enrollStatusTabs, typeTabs],
  );

  const renderCheckTh = () => (
    <th
      scope="col"
      className="tpo-ops-th tpo-ops-th--check temu-product-cell--spu-check"
      onClick={() => {
        if (!shopId || loading || selectableKeys.length === 0) return;
        toggleAll();
      }}
    >
      <input
        type="checkbox"
        aria-label={t(meta.selectAllLabelKey)}
        disabled={!shopId || loading || selectableKeys.length === 0}
        checked={allSelected}
        onClick={(e) => e.stopPropagation()}
        onChange={toggleAll}
      />
    </th>
  );

  const renderCheckTd = (key, row, rowSpan) => (
    <td
      rowSpan={rowSpan}
      className="tpo-ops-td tpo-ops-td--check temu-product-cell temu-product-cell--spu-check"
      onClick={() => toggleOne(key, row)}
    >
      <input
        type="checkbox"
        aria-label={`选择 ${key}`}
        checked={selectedKeys.has(key)}
        onClick={(e) => e.stopPropagation()}
        onChange={() => toggleOne(key, row)}
      />
    </td>
  );

  const renderEnrollRows = () => rows.flatMap((row, index) => {
    const key = rowKey(row, pageView, index);
    const selected = selectedKeys.has(key);
    const skcItems = enrollSkcItems(row);
    const rowSpan = Math.max(1, skcItems.length);
    const sessions = Array.isArray(row.sessions) ? row.sessions : [];
    const activityLabel = row.activityThematicName || row.name || '—';
    const productTitle = row.title || row.productName || fixedUi('未命名货品');
    const categoryLabel = row.categoryLabel || '—';
    const previewSessions = sessions.slice(0, 3);
    const activityStatusLabel = row.activityStatusLabel || row.sessionStatusLabel || row.status || '—';
    const activityStatusCode = row.activityStatus ?? row.sessionStatus;
    const enrollStatusLabel = row.enrollStatusLabel || '—';
    const soldTone = soldStatusTone(row.soldStatus);
    const soldLabel = fixedUi(row.soldStatusLabel || '正常');

    return skcItems.map((skc, skcIdx) => {
      const isFirst = skcIdx === 0;
      const currency = skc.currency || row.currency;
      const activityDiscount = formatActivityDiscount(skc.activityPrice, skc.supplyPrice);
      return (
        <tr
          key={`${key}-skc-${skcIdx}-${skc.skcName || 'empty'}`}
          className={`temu-product-row temu-draft-row tpo-row--clickable${isFirst ? '' : ' temu-draft-row--skc-child'}${selected ? ' is-selected' : ''}`}
          onClick={(e) => onRowClickSelect(e, key, row)}
        >
          {isFirst ? (
            <>
              {renderCheckTd(key, row, rowSpan)}
              <td rowSpan={rowSpan} className="temu-product-cell temu-product-cell--info">
                <div className="temu-product-info temu-product-info--text-only temu-draft-product-info">
                  <div className="temu-product-info__text">
                    <HoverCopyField
                      value={productTitle}
                      className="temu-product-info__title"
                      popoverCopy
                    />
                    <HoverCopyField
                      label="SPU"
                      value={row.productId != null ? String(row.productId) : ''}
                      className="temu-product-info__meta"
                      separator=": "
                    />
                    <div className="temu-product-info__meta">
                      分类: {categoryLabel}
                    </div>
                  </div>
                </div>
              </td>
            </>
          ) : null}
          <td className="temu-product-cell temu-product-cell--spec">
            <div className="temu-product-spec temu-draft-skc">
              <TemuProductThumb imageUrl={skc.imageUrl || row.productImageUrl} />
              <div className="temu-product-spec__text">
                <div className="temu-product-spec__color">
                  {skc.colorLabel || '—'}
                </div>
                <HoverCopyField
                  label="SKC"
                  value={skc.skcName || ''}
                  className="temu-product-spec__skc"
                  separator=": "
                />
                <HoverCopyField
                  label={fixedUi("货号")}
                  value={skc.supplierCode || ''}
                  className="temu-product-spec__skc"
                  separator=": "
                  emptyText="—"
                />
              </div>
            </div>
          </td>
          <td className="temu-product-cell temu-product-cell--price temu-product-cell--supply-price">
            {formatMoney(skc.supplyPrice, currency)}
          </td>
          <td className="temu-product-cell temu-product-cell--price temu-product-cell--activity-price">
            <div className="tpo-marketing-enroll-activity-price">
              <div className="tpo-marketing-enroll-activity-price__value">
                {formatMoney(skc.activityPrice, currency)}
              </div>
              {activityDiscount ? (
                <div
                  className={`tpo-marketing-enroll-activity-price__discount${activityDiscount === '原价' ? ' tpo-marketing-enroll-activity-price__discount--full' : ''}`}
                >
                  {activityDiscount}
                </div>
              ) : null}
            </div>
          </td>
          {isFirst ? (
            <>
              <td rowSpan={rowSpan} className="temu-product-cell temu-product-cell--enroll-info">
                <div className="tpo-marketing-enroll-info">
                  <div>
                    <span className="tpo-marketing-enroll-info__label">{fixedUi("报名时间：")}</span>
                    <span>{row.enrollTime || '—'}</span>
                  </div>
                  <div>
                    <span className="tpo-marketing-enroll-info__label">{fixedUi("活动开始时间：")}</span>
                    <span>{row.sessionStartTime || row.startTime || '—'}</span>
                  </div>
                  <div>
                    <span className="tpo-marketing-enroll-info__label">{fixedUi("活动结束时间：")}</span>
                    <span>{row.sessionEndTime || row.endTime || '—'}</span>
                  </div>
                  <div>
                    <span className="tpo-marketing-enroll-info__label">{fixedUi("活动名称：")}</span>
                    <span>{activityLabel}</span>
                  </div>
                </div>
              </td>
              <td rowSpan={rowSpan} className="temu-product-cell temu-product-cell--activity-status">
                <span className={`tpo-records__status tpo-records__status--${activityStatusTone(activityStatusCode ?? activityStatusLabel)}`}>
                  {activityStatusLabel}
                </span>
              </td>
              <td rowSpan={rowSpan} className="temu-product-cell temu-product-cell--sessions">
                {sessions.length ? (
                  <div className="tpo-marketing-enroll-sessions">
                    {previewSessions.map((session, sIdx) => {
                      const statusText = session.enrollStatusLabel || enrollStatusLabel;
                      const statusTone = enrollStatusTone(session.enrollStatus ?? row.enrollStatus ?? statusText);
                      return (
                        <div
                          key={session.sessionId || sIdx}
                          className="tpo-marketing-enroll-session"
                        >
                          <span>{session.siteName || '—'}</span>
                          <span>{session.timeLabel || [session.startTime, session.endTime].filter(Boolean).join(' - ') || '—'}</span>
                          <span className={`tpo-marketing-enroll-session__status tpo-marketing-enroll-session__status--${statusTone}`}>
                            {statusText}
                          </span>
                        </div>
                      );
                    })}
                    {sessions.length > previewSessions.length ? (
                      <button
                        type="button"
                        className="tpo-marketing-enroll-sessions__more"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSessionsModal({
                            title: [
                              row.productId != null && row.productId !== ''
                                ? `SPU: ${row.productId}`
                                : null,
                              activityLabel !== '—' ? activityLabel : null,
                            ].filter(Boolean).join(' · ') || fixedUi("活动场次"),
                            sessions,
                            enrollStatusLabel,
                          });
                        }}
                      >
                        {fixedUi('更多')}({sessions.length})
                      </button>
                    ) : null}
                  </div>
                ) : '—'}
              </td>
              <td rowSpan={rowSpan} className="temu-product-cell temu-product-cell--stock">
                <div>{fixedUi('提报:')} {row.activityStock != null ? row.activityStock : '—'}</div>
                <div>{fixedUi('剩余:')} {row.remainingActivityStock != null ? row.remainingActivityStock : '—'}</div>
                <div className={`tpo-marketing-sold-status tpo-marketing-sold-status--${soldTone}`}>
                  {soldLabel}
                </div>
              </td>
            </>
          ) : null}
        </tr>
      );
    });
  });

  return (
    <>
      <TemuSyncProgressSlot kinds={meta.syncKinds} />
      <ProductFilterCard
        toolbar={(
          <TemuOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={loading || syncing || exporting}
            syncKinds={meta.syncKinds}
            quotaKind={meta.quotaKind}
            onSync={onSync}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!shopId || syncing}
            lastSyncedLabel={lastSyncedLabel}
            syncHint={t(meta.syncHintKey)}
            exportLabel={opsExportLabel(exporting)}
            exportDisabled={!shopId || exporting}
            exportMenu={buildOpsExportMenu({
              exporting,
              filteredCount: total,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: () => exportRows(rows),
              onExportSelected: () => exportRows(resolveSelectedRows()),
            })}
            batchOpsMenu={batchOpsMenu}
          />
        )}
      >
        <div className="tpo-filter-bar">
          <div className="tpo-filter-bar__fields">
            <div className="tpo-field tpo-field--inline tpo-field--w300">
              <span className="tpo-field__label">{fixedUi("关键词")}</span>
              <input
                className="tpo-input"
                type="search"
                value={filterDraft.keyword}
                disabled={!shopId}
                placeholder={t(meta.keywordPlaceholderKey)}
                onChange={(e) => setFilterDraft((prev) => ({ ...prev, keyword: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
              />
            </div>
            {pageView === 'activities' ? (
              <div className="tpo-field tpo-field--inline tpo-field--status">
                <span className="tpo-field__label">{fixedUi("报名方式")}</span>
                <select
                  className="tpo-select tpo-select--status tpo-select--compact"
                  value={filterDraft.enrollSource}
                  disabled={!shopId}
                  onChange={(e) => setFilterDraft((prev) => ({ ...prev, enrollSource: e.target.value }))}
                >
                  <option value="">{fixedUi("全部")}</option>
                  <option value="1">{fixedUi("自主报名")}</option>
                  <option value="0">{t('marketing.srcInvite')}</option>
                </select>
              </div>
            ) : (
              <>
                <div className="tpo-field tpo-field--inline tpo-field--status">
                  <span className="tpo-field__label">{t('marketing.enrollStatus')}</span>
                  <select
                    className="tpo-select tpo-select--status tpo-select--compact"
                    value={filterDraft.enrollStatus}
                    disabled={!shopId}
                    onChange={(e) => setFilterDraft((prev) => ({ ...prev, enrollStatus: e.target.value }))}
                  >
                    {ENROLL_STATUS_FILTER_OPTIONS.map((opt) => (
                      <option key={opt.value || 'all'} value={opt.value}>{opt.labelKey ? t(opt.labelKey) : opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="tpo-field tpo-field--inline tpo-field--time-range">
                  <span className="tpo-field__label">{fixedUi("报名时间")}</span>
                  <input
                    className="tpo-input"
                    type="date"
                    value={filterDraft.enrollTimeBegin}
                    disabled={!shopId}
                    onChange={(e) => setFilterDraft((prev) => ({ ...prev, enrollTimeBegin: e.target.value }))}
                  />
                  <span className="tpo-time-range__sep">{fixedUi("至")}</span>
                  <input
                    className="tpo-input"
                    type="date"
                    value={filterDraft.enrollTimeEnd}
                    disabled={!shopId}
                    onChange={(e) => setFilterDraft((prev) => ({ ...prev, enrollTimeEnd: e.target.value }))}
                  />
                </div>
              </>
            )}
          </div>
          <div className="tpo-filter-bar__actions">
            <button type="button" className="tpo-btn tpo-btn--secondary tpo-btn--sm" disabled={!shopId} onClick={onReset}>
              {fixedUi("重置")}
            </button>
            <button
              type="button"
              className="tpo-btn tpo-btn--primary tpo-btn--sm"
              disabled={!shopId || loading}
              onClick={onQuery}
            >
              {fixedUi("查询")}
            </button>
          </div>
        </div>
        {error ? <div className="tpo-ops-error" role="alert">{error}</div> : null}
      </ProductFilterCard>

      <ProductListCard
        tabs={listTabs}
        loading={Boolean(shopId) && loading}
        loadingLabel={t(meta.loadingLabelKey)}
      >
        {pageView === 'enrolls' ? (
          <div className={`wf-tasks-page platform-product-page wf-tasks-page--embedded platform-product-page--ops-chrome${loading ? ' platform-product-page--loading' : ''}`}>
            <div className="wf-tasks-table-panel">
              <div className="wf-tasks-table-wrap tpo-ops-table-wrap" role="region" aria-live="polite">
                <table className="wf-tasks-table platform-product-table platform-product-table--temu platform-product-table--drafts platform-product-table--records platform-product-table--marketing-enrolls tpo-ops-table--sticky-head">
                  <colgroup>
                    <col className="temu-product-col--check" />
                    <col className="temu-product-col--info" />
                    <col className="temu-product-col--skc" />
                    <col className="temu-product-col--price temu-product-col--supply-price" />
                    <col className="temu-product-col--price temu-product-col--activity-price" />
                    <col className="temu-product-col--enroll-info" />
                    <col className="temu-product-col--activity-status" />
                    <col className="temu-product-col--sessions" />
                    <col className="temu-product-col--stock" />
                  </colgroup>
                  <thead>
                    <tr>
                      {renderCheckTh()}
                      <th scope="col">{fixedUi("产品信息")}</th>
                      <th scope="col">SKC</th>
                      <th scope="col" className="temu-product-th--supply-price">{fixedUi("供货价")}</th>
                      <th scope="col" className="temu-product-th--activity-price">{fixedUi("活动价")}</th>
                      <th scope="col">{fixedUi("报名信息")}</th>
                      <th scope="col" className="temu-product-th--activity-status">{fixedUi("活动状态")}</th>
                      <th scope="col">{fixedUi("活动场次")}</th>
                      <th scope="col" className="temu-product-th--stock">{fixedUi("提报/剩余")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!shopId || loading || rows.length === 0 ? (
                      <tr>
                        <td colSpan={meta.colSpan} className="wf-tasks-table__empty">
                          <div className="funding-empty-state">
                            <p className="funding-empty-state__title">{emptyTitle}</p>
                            {emptyHint ? (
                              <p className="funding-empty-state__description">{emptyHint}</p>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      renderEnrollRows()
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : (
          <div className="tpo-drafts__table-wrap tpo-ops-table-wrap">
            <table className="tpo-publish__table tpo-drafts__table tpo-ops-table tpo-ops-table--sticky-head">
              <colgroup>
                <col className="tpo-ops-col tpo-ops-col--check temu-product-col--check" />
              </colgroup>
              <thead>
                <tr>
                  {renderCheckTh()}
                  <th scope="col">{fixedUi("活动名称")}</th>
                  <th scope="col">{fixedUi("类型")}</th>
                  <th scope="col">{fixedUi("报名方式")}</th>
                  <th scope="col">{fixedUi("报名截止")}</th>
                  <th scope="col">{fixedUi("活动起止")}</th>
                  <th scope="col">{fixedUi("站点")}</th>
                  <th scope="col">{fixedUi("操作")}</th>
                </tr>
              </thead>
              <tbody>
                {!shopId || loading || rows.length === 0 ? (
                  <tr>
                    <td colSpan={meta.colSpan} className="tpo-certs-empty-cell">
                      <div className="tpo-certs-empty">
                        <p className="tpo-certs-empty__title">{emptyTitle}</p>
                        {emptyHint ? <p className="tpo-certs-empty__hint">{emptyHint}</p> : null}
                      </div>
                    </td>
                  </tr>
                ) : (
                  rows.map((row, index) => {
                    const key = rowKey(row, pageView, index);
                    const selected = selectedKeys.has(key);
                    return (
                      <tr
                        key={key}
                        className={`tpo-row--clickable${selected ? ' is-selected' : ''}`}
                        onClick={(e) => onRowClickSelect(e, key, row)}
                      >
                        {renderCheckTd(key, row)}
                        <td>{row.name || row.activityName || '—'}</td>
                        <td>{activityTypeDisplayLabel(row)}</td>
                        <td>{enrollSourceDisplayLabel(row)}</td>
                        <td>{row.enrollDeadline || '—'}</td>
                        <td>
                          {[row.startTime, row.endTime].filter(Boolean).join(' ~ ') || '—'}
                        </td>
                        <td><MarketingSitesCell row={row} /></td>
                        <td>
                          <div className="tpo-marketing-list__ops">
                            <button
                              type="button"
                              className="tpo-btn tpo-btn--ghost tpo-btn--sm"
                              onClick={() => setDrawerActivity(row)}
                            >
                              {fixedUi("查看")}
                            </button>
                            <button
                              type="button"
                              className="tpo-btn tpo-btn--primary tpo-btn--sm"
                              onClick={() => openEnrollInNewTab(row)}
                            >
                              {fixedUi("报名")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
        <TemuOpsPager
          page={page}
          pageSize={pageSize}
          total={total}
          loading={loading}
          disabled={!shopId}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t(meta.pagerLabelKey)}
        />
      </ProductListCard>

      {pageView === 'activities' ? (
        <TemuMarketingActivityDrawer
          open={Boolean(drawerActivity)}
          shopId={shopId}
          activity={drawerActivity}
          onClose={() => setDrawerActivity(null)}
        />
      ) : null}

      <MarketingEnrollSessionsModal
        open={Boolean(sessionsModal)}
        title={sessionsModal?.title || fixedUi("活动场次")}
        sessions={sessionsModal?.sessions || []}
        enrollStatusLabel={sessionsModal?.enrollStatusLabel || ''}
        onClose={() => setSessionsModal(null)}
      />
    </>
  );
}
